import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { cn } from "@/lib/utils";
import { LINE_STATUS } from "@/lib/status";
import { fmtDuree, fmtNum, fmtPct, TONE_BG, TONE_TEXT, trsTone } from "@/lib/format";
import { Panel } from "@/components/common/Panel";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";

const AXIS = { fill: "#94a3b8", fontSize: 10, fontFamily: "JetBrains Mono" };

const PctCell = ({ v }) => {
  const t = trsTone(v);
  return (
    <div className="flex items-center gap-1.5 justify-end">
      <div className="w-8 h-1.5 bg-muted rounded-sm overflow-hidden"><div className={cn("h-full", TONE_BG[t])} style={{ width: `${Math.min(v || 0, 100)}%` }} /></div>
      <span className={cn("font-mono w-14 text-right", TONE_TEXT[t])}>{fmtPct(v)}</span>
    </div>
  );
};

const pct = (k, header) => ({ key: k, header, align: "right", render: (r) => <PctCell v={r[k]} /> });

const columns = (selected) => [
  { key: "ligne", header: "Ligne", render: (r) => <span className={cn("font-semibold", selected === r.ligne_id && "text-primary")}>{r.ligne}</span> },
  { key: "statut", header: "État", render: (r) => <StatusBadge config={LINE_STATUS} code={r.statut} /> },
  pct("trs", "TRS"), pct("disponibilite", "Dispo."), pct("performance", "Perf."), pct("qualite", "Qualité"),
  { key: "bonnes", header: "Bonnes", align: "right", render: (r) => fmtNum(r.bonnes) },
  { key: "rejets", header: "Rejets", align: "right", render: (r) => <span className={cn(r.rejets > 0 && "text-amber-400")}>{fmtNum(r.rejets)}</span> },
  { key: "of", header: "OF actif", render: (r) => r.of_actif ? <span className="font-mono">{r.of_actif.numero} <span className="text-muted-foreground">· {fmtPct(r.of_actif.avancement_pct, 0)}</span></span> : <span className="text-muted-foreground">—</span> },
  { key: "arret", header: "Arrêt en cours", render: (r) => r.arret_categorie ? <span className={cn("font-mono", r.arret_planifie ? "text-amber-400" : "text-red-400")}>{r.arret_categorie} · {fmtDuree(r.arret_duree_sec)}</span> : <span className="text-muted-foreground">—</span> },
];

const Tip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-sm p-2 text-[11px] font-mono shadow-xl">
      <div className="font-semibold mb-1">{label}</div>
      {payload.map((p) => <div key={p.dataKey} style={{ color: p.fill }}>{p.name} {fmtPct(p.value)}</div>)}
    </div>
  );
};

export const LignesComparaison = ({ lignes, selected, onSelect }) => {
  const actives = lignes.filter((l) => l.statut !== "hors_service");
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1.7fr_1fr] gap-3">
      <Panel title="Comparaison des lignes" subtitle="temps réel · TRS du jour" testId="trs-lignes-panel" bodyClassName="p-0">
        <DataTable testId="trs-lignes-table" columns={columns(selected)} rows={lignes} rowKey={(r) => r.ligne_id} onRowClick={(r) => onSelect(r.ligne_id)} emptyTitle="Aucune ligne configurée" />
        <div className="px-3 py-1.5 border-t border-border/60 text-[10.5px] text-muted-foreground">Cliquez sur une ligne pour afficher son découpage par quart et sa tendance.</div>
      </Panel>
      <Panel title="TRS / D / P / Q par ligne" subtitle="lignes actives" testId="trs-lignes-chart" bodyClassName="p-2">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={actives} margin={{ top: 8, right: 8, left: -20, bottom: 0 }} barGap={2} barCategoryGap="25%">
              <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="ligne" tick={AXIS} axisLine={false} tickLine={false} interval={0} />
              <YAxis domain={[0, 100]} tick={AXIS} axisLine={false} tickLine={false} />
              <Tooltip content={<Tip />} cursor={{ fill: "#1e293b", opacity: 0.4 }} />
              <Legend wrapperStyle={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
              <Bar dataKey="trs" name="TRS" fill="#10b981" />
              <Bar dataKey="disponibilite" name="D" fill="#3b82f6" fillOpacity={0.7} />
              <Bar dataKey="performance" name="P" fill="#8b5cf6" fillOpacity={0.7} />
              <Bar dataKey="qualite" name="Q" fill="#f59e0b" fillOpacity={0.7} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>
    </div>
  );
};
